import { useRef, useCallback } from 'react';

/**
 * A hook that provides a guard to prevent rapid re-execution of async actions.
 * Primarily used to handle React StrictMode double-invocations in development
 * while allowing fresh executions on real remounts or navigation.
 * 
 * @param gracePeriodMs - The time window (in ms) during which subsequent calls are blocked. Defaults to 500ms.
 * @returns { checkGuard, resetGuard }
 */
export function useAsyncGuard(gracePeriodMs = 500) {
  const lastExecutionTimestamp = useRef<number>(0);

  /**
   * Checks if the action should proceed based on the last execution time.
   * If the guard passes, it automatically updates the internal timestamp.
   * 
   * @returns boolean - true if the action should proceed, false if it's blocked.
   */
  const checkGuard = useCallback(() => {
    const now = Date.now();
    if (now - lastExecutionTimestamp.current < gracePeriodMs) {
      return false;
    }
    lastExecutionTimestamp.current = now;
    return true;
  }, [gracePeriodMs]);

  /**
   * Resets the guard, allowing the next call to proceed regardless of the grace period.
   */
  const resetGuard = useCallback(() => {
    lastExecutionTimestamp.current = 0;
  }, []);

  return { checkGuard, resetGuard };
}
